import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import groupBy from 'lodash.groupby';

// Swiper for "Currently Reading" carousel
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Mousewheel } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import Sort from './Sort';
import SortedCatalogue from './SortedCatalogue';
import PopularTopics from './PopularTopics';
import SignupLoginDialog from '../../component/SignupDialog';
import BookCoverPage from './BookCoverPage';

import { catalogueDataFull } from './data';
import { useCurrentlyReading } from '../../hooks/useCurrentlyReading';
import { auth } from '../../config/firebase';

import './books.css';

const SORT_OPTIONS = {
  Default: 'Default',
  Volume: 'Volume',
  Year: 'Year',
};

const LANGUAGE_OPTIONS = {
  English: 'English',
  Hindi: 'Hindi',
  Marathi: 'Marathi',
  Gujarati: 'Gujarati',
};

const STATUS_OPTIONS = {
  allBooks: 'All Books',
  reading: 'Reading',
  notReading: 'Not Reading',
};

export default function Books() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentlyReading, toggleBook } = useCurrentlyReading();
  const [isSignupPopupOpen, setIsSignupPopupOpen] = useState(false);

  // --- MODIFICATION: Initialize state from localStorage or defaults ---
  const defaultFilterBy =
    searchParams.get('filterBy') ||
    localStorage.getItem('filterBy') ||
    'English';
  const defalutSortBy =
    searchParams.get('sortBy') || localStorage.getItem('sortBy') || 'Default';

  const [filterByReading, setFilterByReading] = useState(
    () => localStorage.getItem('filterByReading') || 'allBooks'
  );

  // --- MODIFICATION: Persist changes to localStorage ---
  useEffect(() => {
    // This effect runs whenever a user changes a filter or sort option.
    localStorage.setItem('filterBy', defaultFilterBy);
    localStorage.setItem('sortBy', defalutSortBy);
    localStorage.setItem('filterByReading', filterByReading);

    // Also update the URL search params to reflect the current state.
    const currentParams = Object.fromEntries(searchParams.entries());
    setSearchParams({
      ...currentParams,
      filterBy: defaultFilterBy,
      sortBy: defalutSortBy,
    });
  }, [defaultFilterBy, defalutSortBy, filterByReading, setSearchParams]);

  const handleFilter = (e) => {
    const value = e.target.value;
    const currentParams = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...currentParams, filterBy: value });
  };

  const handleSort = (e) => {
    const value = e.target.value;
    const currentParams = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...currentParams, sortBy: value });
  };

  const handleReadingFilter = (e) => {
    setFilterByReading(e.target.value);
  };

  const handleToggle = (book) => {
    const user = auth.currentUser;
    if (!user) setIsSignupPopupOpen(true);
    toggleBook(book);
  };

  const groupByDefault = () => 'Books';

  const groupByVolume = (book) => `Volume ${book.Volume}`;

  const groupByYear = (book) => {
    if (book.Year >= 1910 && book.Year <= 1919) return '1910s';
    if (book.Year >= 1920 && book.Year <= 1929) return '1920s';
    if (book.Year >= 1930 && book.Year <= 1939) return '1930s';
    if (book.Year >= 1940 && book.Year <= 1949) return '1940s';
    if (book.Year >= 1950 && book.Year <= 1959) return '1950s';
  };

  var langCode;

  switch (defaultFilterBy) {
    case 'Marathi':
      langCode = '/MR/';
      break;
    case 'Gujarati':
      langCode = '/GU/';
      break;
    case 'Hindi':
      langCode = '/HI/';
      break;
    default:
      langCode = '/EN/';
      break;
  }

  let catalogueData = catalogueDataFull.filter((item) =>
    item.Path.includes(langCode)
  );

  catalogueData = catalogueData.map((book) => {
    const readingEntry = currentlyReading.find((e) => e.link === book.Path);
    if (readingEntry) {
      return { ...book, isReading: true, updatedAt: readingEntry.updatedAt };
    }
    return { ...book, isReading: false, updatedAt: null };
  });

  // Get the list of books being read, and then sort it immediately.
  const currentlyReadingBooks = catalogueData
    .filter((book) => book.isReading)
    .sort((a, b) => b.updatedAt - a.updatedAt);

  catalogueData =
    filterByReading === 'allBooks'
      ? catalogueData
      : catalogueData.filter((book) =>
          filterByReading === 'reading' ? book.isReading : !book.isReading
        );

  const groupedData =
    defalutSortBy === 'Default'
      ? groupBy(catalogueData, groupByDefault)
      : defalutSortBy === 'Year'
        ? groupBy(catalogueData, groupByYear)
        : groupBy(catalogueData, groupByVolume);

  const sortedData = Object.entries(groupedData).sort((a, b) =>
    parseFloat(a[0]) < parseFloat(b[0]) ? -1 : 1
  );

  return (
    <Row>
      <div className="filter-container">
        <Sort
          label="Language"
          options={LANGUAGE_OPTIONS}
          defaultValue={defaultFilterBy}
          onChange={handleFilter}
        />

        <Sort
          label="Sort"
          options={SORT_OPTIONS}
          defaultValue={defalutSortBy}
          onChange={handleSort}
        />

        <Sort
          label="Status"
          options={STATUS_OPTIONS}
          defaultValue={filterByReading}
          onChange={handleReadingFilter}
        />
      </div>

      <Col>
        {/* "Currently Reading" Carousel */}
        {currentlyReadingBooks.length > 0 && (
          <section className="mb-12" aria-labelledby="currently-reading-header">
            <h2
              id="currently-reading-header"
              className="mb-6 rounded-md bg-indigo-600 px-4 py-2 text-2xl font-bold text-white shadow-md"
            >
              Currently Reading
            </h2>
            <div className="relative w-full">
              <Swiper
                modules={[Navigation, Pagination, Mousewheel]}
                navigation={true}
                pagination={{ clickable: true }}
                mousewheel={{
                  forceToAxis: true,
                }}
                className="!pb-10"
                breakpoints={{
                  320: { slidesPerView: 2, spaceBetween: 16 },
                  640: { slidesPerView: 3, spaceBetween: 16 },
                  768: { slidesPerView: 4, spaceBetween: 16 },
                  1024: { slidesPerView: 6, spaceBetween: 16 },
                }}
              >
                {currentlyReadingBooks.map((catalog) => (
                  <SwiperSlide key={catalog.Path}>
                    <BookCoverPage
                      link={catalog.Path}
                      imageSrc={`https://baws.in/assets/catalog/${catalog.Filename}`}
                      bookLabel={catalog.Label}
                      endPage={catalog.EndPage}
                      isReading={catalog.isReading}
                      onToggle={handleToggle}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </section>
        )}

        <SortedCatalogue
          sortedData={sortedData}
          sortBy={defalutSortBy}
          onToggle={handleToggle}
        />
        <PopularTopics />
      </Col>

      {isSignupPopupOpen && (
        <SignupLoginDialog
          showSignupPopup={isSignupPopupOpen}
          setIsSignupPopupOpen={setIsSignupPopupOpen}
        />
      )}
    </Row>
  );
}
